import { inject } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { CLIENT_STATE } from "app/services/client.state";
import { OrganizationService } from "app/services/organization.service";
import { SignalRService } from "app/services/signal-r.service";
import { TimeTrackingWorkerService } from "app/services/time-tracking-worker.service";
import { secondsToTime2 } from "app/utils/seconds-to-time";
import { DateTime } from "luxon";
import { BehaviorSubject, filter, map, shareReplay, take } from "rxjs";

export class TimeTrackingServiceBase {
  protected readonly _duration$ = new BehaviorSubject<number>(0);
  protected readonly _error$ = new BehaviorSubject<string | null>(null);
  // protected readonly _currentDate$ = new BehaviorSubject<DateTime>(DateTime.now());
  protected readonly signalService = inject(SignalRService);
  protected readonly organizationService = inject(OrganizationService);
  protected readonly timeTrackingWorkerService = inject(TimeTrackingWorkerService);
  sessionId: string | null = null;
  protected _activeSessionStart: string = new Date().toJSON();
  static DELTA = 60;
  protected readonly _isPlay$ = new BehaviorSubject<boolean>(false);
  protected _timeWorkedState = 0;
  protected readonly _durationActive$ = new BehaviorSubject<number>(0);
  private readonly clientState$ = inject(CLIENT_STATE);
  protected readonly snackBar = inject(MatSnackBar);
  // protected lastCommit: string = DateTime.now().toJSON();

  // durationLastCommit = 0;
  // durationActiveLastCommit = 0;

  // readonly currentDate$ = this._currentDate$.pipe(
  //   shareReplay({ bufferSize: 1, refCount: true })
  // );

  readonly duration$ = this._duration$.asObservable().pipe(
    filter(duration => duration >= 0),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // readonly durationActive$ = this.currentDate$.pipe(
  //   map(currentDate => currentDate.diff(DateTime.fromISO(this._activeSessionStart), 'seconds').seconds),
  //   filter(duration => duration > 0),
  //   shareReplay({ bufferSize: 1, refCount: true })
  // );

  readonly durationActive$ = this._durationActive$.asObservable().pipe(
    filter(duration => duration > 0),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly error$ = this._error$.asObservable().pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly isPlay$ = this._isPlay$.asObservable().pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );

  get isPlayValue() {
    return this._isPlay$.value;
  }

  // readonly isReconnected$ = this.signalService.isReconnected$;

  get clientStarted$() {
    return this.signalService.on('ClientStarted').pipe(
      map(args => {
        const [orgId, sessionId, objectId, objectKind, startedAt, connectId] = args;
        return { orgId, sessionId, objectId, objectKind, startedAt, connectId };
      })
    );
  }

  get clientStopped$() {
    return this.signalService.on('ClientStopped').pipe(
      map(args => {
        const [orgId, sessionId, stoppedAt, timeWorked, connectId] = args;
        return { orgId, sessionId, stoppedAt, timeWorked, connectId };
      })
    );
  }

  get clientPaused$() {
    return this.signalService.on('ClientPaused');
  }

  get clientTimeWorkedUpdated$() {
    return this.signalService.on('ClientTimeWorkedUpdated').pipe(
      map(args => {
        const [orgId, timeWorked, commitedAtt] = args;
        return { orgId, timeWorked, commitedAtt };
      }),
    );
  }

  get clientSessionObjectChanged$() {
    return this.signalService.on('ClientSessionObjectChanged').pipe(
      map(args => {
        const [orgId, sessionId, objectId, objectKind, connectId] = args;
        return { orgId, sessionId, objectId, objectKind, connectId };
      })
    );
  }

  get clientSessionResumed$() {
    return this.signalService.on('ClientResumed').pipe(
      map(args => {
        const [orgId] = args;
        return { orgId };
      })
    );
  }

  get sessionCreated$() {
    return this.signalService.on('SessionCreated');
  }
  get sessionSubjectChanged$() {
    return this.signalService.on('SessionSubjectChanged');
  }
  get sessionResized$() {
    return this.signalService.on('SessionResized');
  }
  get sessionSwitched$() {
    return this.signalService.on('SessionSwitched');
  }
  get sessionDeleted$() {
    return this.signalService.on('SessionDeleted');
  }

  protected sendToWorker(event: 'start' | 'stop' | null, committedAt: string, starttedAt?: string) {
    this.timeTrackingWorkerService.worker.postMessage({ event, committedAt, starttedAt, isPlay: this._isPlay$.value });
  }

  init(timeWorked: number, committedAt: string) {
    this._error$.next('');
    this._isPlay$.next(false);
    this.sendToWorker('stop', committedAt);
    // this.lastCommit = committedAt;
    this._timeWorkedState = timeWorked;
    this._duration$.next(timeWorked);
  }

  protected reset(err: any) {
    // this.snackBar.open(err, 'Ok');
    this._error$.next(err);
    // reset last state
    this.clientState$.pipe(
      take(1)
    ).subscribe(({ client }) => {
      const { timeWorked } = client;
      this._isPlay$.next(false);
      this.sendToWorker('stop', DateTime.now().toJSON());
      this._durationActive$.next(0);
      this._duration$.next(timeWorked);
    });
  }

  run(sessionId: string, committedAt: string, starttedAt?: string) {
    // this.lastCommit = committedAt;
    this._activeSessionStart = starttedAt || committedAt;
    this.sendToWorker('stop', committedAt);
    this.sessionId = sessionId;
    this._isPlay$.next(true);
    const timeDifference = Math.floor(DateTime.now().set({ millisecond: 0 }).toUTC().diff(DateTime.fromISO(committedAt), 'seconds').seconds);
    this._duration$.next(timeDifference + this._timeWorkedState);
    this.sendToWorker('start', committedAt, starttedAt);
  }

  updateDuration(timeWorked: number, committedAt: string) {
    // console.log(secondsToTime2(timeWorked), committedAt)
    this._timeWorkedState = timeWorked;
    this._duration$.next(timeWorked);
    this.sendToWorker(null, committedAt);
  }
}